import React, { useState, useMemo, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { format } from 'date-fns';
import { NDP6_TAXONOMY } from '../../data/ndp6Taxonomy';

/**
 * The 14 Namibian Ministries (2025 restructuring) plus OP and OPM.
 * Used for ministry selection dropdowns throughout the modal.
 */
const MINISTRIES = [
  { id: 'MODVA', name: 'Defence and Veterans Affairs' },
  { id: 'MHAISS', name: 'Home Affairs, Immigration, Safety and Security' },
  { id: 'MIRT', name: 'International Relations and Trade' },
  { id: 'MFSGM', name: 'Finance and Social Grants Management' },
  { id: 'MAFWLR', name: 'Agriculture, Fisheries, Water and Land Reform' },
  { id: 'MEIYSC', name: 'Education, Innovation, Youth, Sport, Arts and Culture' },
  { id: 'MHSS', name: 'Health and Social Services' },
  { id: 'MIME', name: 'Industrialisation, Mines and Energy' },
  { id: 'MJL', name: 'Justice and Labour Relations' },
  { id: 'MWTI', name: 'Works, Transport and Infrastructure' },
  { id: 'MEFT', name: 'Environment, Forestry and Tourism' },
  { id: 'MURDH', name: 'Urban and Rural Development, Housing' },
  { id: 'MICT', name: 'Information and Communication Technology' },
  { id: 'GEPSW', name: 'Gender Equality, Poverty Eradication and Social Welfare' },
  { id: 'OP', name: 'Office of the President' },
  { id: 'OPM', name: 'Office of the Prime Minister' }
];

/** Decision categories as defined in the domain context. */
const CATEGORIES = [
  { id: 'POLICY', label: 'Policy Initiatives' },
  { id: 'STATUTORY', label: 'Statutory Submissions' },
  { id: 'ADMIN', label: 'Administrative Matters' },
  { id: 'LEGISLATION', label: 'Legislation' },
  { id: 'EMERGENCY', label: 'Emergency Response' },
  { id: 'RATIFICATION', label: 'Treaty/Protocol Ratification' },
  { id: 'BUDGET', label: 'Budget Allocation' }
];

/** Priority levels with associated color tokens. */
const PRIORITIES = [
  { id: 'critical', label: 'Critical', color: 'red', description: 'Requires immediate action' },
  { id: 'high', label: 'High', color: 'amber', description: 'Urgent, time-sensitive' },
  { id: 'medium', label: 'Medium', color: 'blue', description: 'Standard priority' },
  { id: 'standard', label: 'Standard', color: 'slate', description: 'Routine matter' }
];

/** Assigned entity types for directives. */
const ENTITY_TYPES = [
  { id: 'Ministry', label: 'Ministry' },
  { id: 'ED', label: 'Executive Director' },
  { id: 'Committee', label: 'Committee' },
  { id: 'TaskForce', label: 'Task Force' },
  { id: 'Agency', label: 'Agency' }
];

/** Ministry role options for the responsible ministries multi-select. */
const MINISTRY_ROLES = ['Lead', 'Co-lead', 'Support'];

/**
 * Returns modal configuration (header color, title, icon) based on the current mode
 * and whether it is editing an existing item.
 */
function getModalConfig(mode, isEditing) {
  const configs = {
    decision: {
      title: isEditing ? 'Edit Cabinet Decision' : 'New Cabinet Decision',
      icon: FiIcons.FiBookOpen,
      headerBg: isEditing ? 'bg-amber-50' : 'bg-grn-navy/5',
      headerBorder: isEditing ? 'border-amber-200' : 'border-grn-navy/20',
      iconBg: isEditing ? 'bg-amber-100' : 'bg-grn-navy/10',
      iconColor: isEditing ? 'text-amber-600' : 'text-grn-navy',
      titleColor: isEditing ? 'text-amber-900' : 'text-grn-navy',
      accentColor: isEditing ? 'amber' : 'grn-navy'
    },
    directive: {
      title: isEditing ? 'Edit Directive' : 'New Directive',
      icon: FiIcons.FiGitBranch,
      headerBg: isEditing ? 'bg-amber-50' : 'bg-blue-50',
      headerBorder: isEditing ? 'border-amber-200' : 'border-blue-200',
      iconBg: isEditing ? 'bg-amber-100' : 'bg-blue-100',
      iconColor: isEditing ? 'text-amber-600' : 'text-blue-600',
      titleColor: isEditing ? 'text-amber-900' : 'text-blue-900',
      accentColor: isEditing ? 'amber' : 'blue'
    },
    assignment: {
      title: isEditing ? 'Edit Assignment' : 'New Assignment',
      icon: FiIcons.FiUserCheck,
      headerBg: isEditing ? 'bg-amber-50' : 'bg-green-50',
      headerBorder: isEditing ? 'border-amber-200' : 'border-green-200',
      iconBg: isEditing ? 'bg-amber-100' : 'bg-green-100',
      iconColor: isEditing ? 'text-amber-600' : 'text-green-600',
      titleColor: isEditing ? 'text-amber-900' : 'text-green-900',
      accentColor: isEditing ? 'amber' : 'green'
    }
  };
  return configs[mode] || configs.decision;
}

/**
 * Builds the initial form state based on the mode and any existing data for editing.
 */
function buildInitialState(mode, existingData) {
  if (mode === 'decision') {
    return {
      title: existingData?.title || '',
      referenceNumber: existingData?.referenceNumber || '',
      description: existingData?.description || '',
      category: existingData?.category || '',
      priority: existingData?.priority || 'medium',
      originatingMinistry: existingData?.originatingMinistry || '',
      dateDecided: existingData?.dateDecided || '',
      deadline: existingData?.deadline || '',
      legalBasis: existingData?.legalBasis || '',
      responsibleMinistries: existingData?.responsibleMinistries || [],
      ndp6PillarId: existingData?.ndp6PillarId ? String(existingData.ndp6PillarId) : '',
      ndp6ThematicAreaId: existingData?.ndp6ThematicArea || '',
      ndp6FocusArea: existingData?.ndp6FocusArea || '',
      hasBudgetImpact: existingData?.budgetImplication?.hasBudgetImpact || false,
      estimatedCost: existingData?.budgetImplication?.estimatedCost || '',
      fundingSource: existingData?.budgetImplication?.fundingSource || '',
      notes: existingData?.fullText || ''
    };
  }

  if (mode === 'directive') {
    return {
      title: existingData?.title || '',
      description: existingData?.description || '',
      parentDecisionId: existingData?.parentDecisionId || '',
      assignedEntityType: existingData?.assignedEntityType || 'Ministry',
      assignedEntityId: existingData?.assignedEntityId || '',
      assigneeName: existingData?.assigneeName || '',
      assigneeRole: existingData?.assigneeRole || '',
      deadline: existingData?.deadline || '',
      targetMetric: existingData?.targetMetric || '',
      milestones: existingData?.milestones || [],
      notes: ''
    };
  }

  // assignment mode
  return {
    taskDescription: existingData?.taskDescription || '',
    parentDirectiveId: existingData?.parentDirectiveId || '',
    assigneeName: existingData?.assignedTo?.name || '',
    assigneeRole: existingData?.assignedTo?.role || '',
    assigneeTitle: existingData?.assignedTo?.title || '',
    assigneeMinistry: existingData?.assignedTo?.ministry || '',
    assigneeDirectorate: existingData?.assignedTo?.directorate || '',
    assigneeEmail: existingData?.assignedTo?.email || '',
    deadline: existingData?.deadline || '',
    checklistItems: existingData?.checklistItems || [],
    notes: ''
  };
}

/**
 * CabinetDecisionModal - Modal for creating/editing cabinet decisions, directives, and assignments.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {'decision'|'directive'|'assignment'} props.mode - Which form variant to render
 * @param {Object} [props.existingData] - Pre-populated data when editing an existing item
 */
export const CabinetDecisionModal = ({ isOpen, onClose, mode = 'decision', existingData = null }) => {
  const isEditing = !!existingData;
  const config = getModalConfig(mode, isEditing);

  const [formData, setFormData] = useState(() => buildInitialState(mode, existingData));
  const [errors, setErrors] = useState({});

  // --- NDP6 cascading dropdown data ---

  const selectedPillar = useMemo(() => {
    if (!formData.ndp6PillarId) return null;
    return NDP6_TAXONOMY.pillars.find(p => String(p.id) === formData.ndp6PillarId) || null;
  }, [formData.ndp6PillarId]);

  const thematicAreas = useMemo(() => {
    return selectedPillar?.thematicAreas || [];
  }, [selectedPillar]);

  const selectedThematic = useMemo(() => {
    if (!formData.ndp6ThematicAreaId) return null;
    return thematicAreas.find(t => String(t.id) === formData.ndp6ThematicAreaId) || null;
  }, [formData.ndp6ThematicAreaId, thematicAreas]);

  const focusAreas = useMemo(() => {
    return selectedThematic?.focusAreas || [];
  }, [selectedThematic]);

  // --- Field updaters ---

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for the field being updated
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  // --- Ministry multi-select handlers ---

  const toggleMinistry = useCallback((ministryId) => {
    setFormData(prev => {
      const exists = prev.responsibleMinistries.find(m => m.ministryId === ministryId);
      if (exists) {
        return {
          ...prev,
          responsibleMinistries: prev.responsibleMinistries.filter(m => m.ministryId !== ministryId)
        };
      }
      const ministry = MINISTRIES.find(m => m.id === ministryId);
      return {
        ...prev,
        responsibleMinistries: [
          ...prev.responsibleMinistries,
          { ministryId, role: 'Support', ministryName: ministry?.name || ministryId }
        ]
      };
    });
  }, []);

  const updateMinistryRole = useCallback((ministryId, role) => {
    setFormData(prev => ({
      ...prev,
      responsibleMinistries: prev.responsibleMinistries.map(m =>
        m.ministryId === ministryId ? { ...m, role } : m
      )
    }));
  }, []);

  // --- Milestone handlers (directive mode) ---

  const addMilestone = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { id: `MS-${Date.now()}`, title: '', deadline: '', status: 'Pending' }
      ]
    }));
  }, []);

  const updateMilestone = useCallback((index, field, value) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map((ms, i) =>
        i === index ? { ...ms, [field]: value } : ms
      )
    }));
  }, []);

  const removeMilestone = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  }, []);

  // --- Checklist handlers (assignment mode) ---

  const addChecklistItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      checklistItems: [
        ...prev.checklistItems,
        { id: `CK-${Date.now()}`, item: '', completed: false }
      ]
    }));
  }, []);

  const updateChecklistItem = useCallback((index, value) => {
    setFormData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems.map((ci, i) =>
        i === index ? { ...ci, item: value } : ci
      )
    }));
  }, []);

  const removeChecklistItem = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems.filter((_, i) => i !== index)
    }));
  }, []);

  // --- Validation ---

  const validate = useCallback(() => {
    const newErrors = {};

    if (mode === 'decision') {
      if (!formData.title.trim()) newErrors.title = 'Title is required';
      if (!formData.category) newErrors.category = 'Category is required';
      if (!formData.priority) newErrors.priority = 'Priority is required';
      if (!formData.dateDecided) newErrors.dateDecided = 'Decision date is required';
      if (!formData.deadline) newErrors.deadline = 'Deadline is required';
      if (formData.responsibleMinistries.length === 0) {
        newErrors.responsibleMinistries = 'At least one responsible ministry is required';
      }
    }

    if (mode === 'directive') {
      if (!formData.title.trim()) newErrors.title = 'Title is required';
      if (!formData.parentDecisionId.trim()) newErrors.parentDecisionId = 'Parent decision is required';
      if (!formData.assignedEntityId) newErrors.assignedEntityId = 'Assigned entity is required';
      if (!formData.deadline) newErrors.deadline = 'Deadline is required';
    }

    if (mode === 'assignment') {
      if (!formData.taskDescription.trim()) newErrors.taskDescription = 'Task description is required';
      if (!formData.parentDirectiveId.trim()) newErrors.parentDirectiveId = 'Parent directive is required';
      if (!formData.assigneeName.trim()) newErrors.assigneeName = 'Assignee name is required';
      if (!formData.assigneeMinistry) newErrors.assigneeMinistry = 'Assignee ministry is required';
      if (!formData.deadline) newErrors.deadline = 'Deadline is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [mode, formData]);

  // --- Submit ---

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;

    const timestamp = new Date().toISOString();

    if (mode === 'decision') {
      const decision = {
        id: existingData?.id || `CAB-${format(new Date(), 'yyyy')}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
        referenceNumber: formData.referenceNumber || `CAB/${Math.floor(Math.random() * 99) + 1}/${format(new Date(), 'yyyy')}`,
        title: formData.title,
        description: formData.description,
        fullText: formData.notes,
        category: formData.category,
        priority: formData.priority,
        originatingMinistry: formData.originatingMinistry,
        dateDecided: formData.dateDecided,
        deadline: formData.deadline,
        legalBasis: formData.legalBasis,
        responsibleMinistries: formData.responsibleMinistries,
        leadMinistryId: formData.responsibleMinistries.find(m => m.role === 'Lead')?.ministryId || formData.responsibleMinistries[0]?.ministryId,
        ndp6PillarId: formData.ndp6PillarId ? Number(formData.ndp6PillarId) : null,
        ndp6Pillar: selectedPillar?.name || null,
        ndp6ThematicArea: selectedThematic?.name || null,
        ndp6FocusArea: formData.ndp6FocusArea || null,
        budgetImplication: {
          hasBudgetImpact: formData.hasBudgetImpact,
          estimatedCost: formData.estimatedCost ? Number(formData.estimatedCost) : 0,
          fundingSource: formData.fundingSource
        },
        status: 'Pending',
        overallProgress: 0,
        totalDirectives: 0,
        completedDirectives: 0,
        overdueDirectives: 0,
        escalationLevel: 0,
        createdBy: "Prime Minister's Office",
        lastUpdated: timestamp
      };
      console.log(`[CabinetDecisionModal] ${isEditing ? 'Updated' : 'Created'} decision:`, decision);
    }

    if (mode === 'directive') {
      const directive = {
        id: existingData?.id || `DIR-${format(new Date(), 'yyyy')}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
        parentDecisionId: formData.parentDecisionId,
        title: formData.title,
        description: formData.description,
        assignedEntityType: formData.assignedEntityType,
        assignedEntityId: formData.assignedEntityId,
        assignedEntityName: MINISTRIES.find(m => m.id === formData.assignedEntityId)?.name || formData.assignedEntityId,
        assigneeName: formData.assigneeName,
        assigneeRole: formData.assigneeRole,
        deadline: formData.deadline,
        targetMetric: formData.targetMetric,
        milestones: formData.milestones.filter(ms => ms.title.trim()),
        status: 'Pending',
        priority: 'high',
        progress: 0,
        createdDate: format(new Date(), 'yyyy-MM-dd'),
        lastUpdated: timestamp
      };
      console.log(`[CabinetDecisionModal] ${isEditing ? 'Updated' : 'Created'} directive:`, directive);
    }

    if (mode === 'assignment') {
      const assignment = {
        id: existingData?.id || `ASG-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
        parentDirectiveId: formData.parentDirectiveId,
        taskDescription: formData.taskDescription,
        assignedTo: {
          name: formData.assigneeName,
          role: formData.assigneeRole,
          title: formData.assigneeTitle,
          ministry: formData.assigneeMinistry,
          directorate: formData.assigneeDirectorate,
          email: formData.assigneeEmail
        },
        deadline: formData.deadline,
        checklistItems: formData.checklistItems.filter(ci => ci.item.trim()),
        status: 'Pending',
        priority: 'high',
        progress: 0,
        assignedDate: format(new Date(), 'yyyy-MM-dd'),
        lastUpdated: timestamp
      };
      console.log(`[CabinetDecisionModal] ${isEditing ? 'Updated' : 'Created'} assignment:`, assignment);
    }

    onClose();
  }, [mode, formData, existingData, isEditing, validate, onClose, selectedPillar, selectedThematic]);

  // --- Render helpers ---

  /** Renders the inline validation error for a given field. */
  const renderError = (field) => {
    if (!errors[field]) return null;
    return (
      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="text-xs flex-shrink-0" />
        {errors[field]}
      </p>
    );
  };

  /** Renders a form field label with optional required marker. */
  const renderLabel = (text, required = false) => (
    <label className="block text-xs font-medium text-slate-600 mb-2">
      {text} {required && <span className="text-red-500">*</span>}
    </label>
  );

  /** Renders the section heading used within the form body. */
  const renderSectionHeading = (text) => (
    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">{text}</h3>
  );

  // --- Decision mode form ---

  const renderDecisionForm = () => (
    <>
      {/* Title and Reference */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {renderLabel('Decision Title', true)}
          <Input
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="e.g., National Housing Development Programme Acceleration"
          />
          {renderError('title')}
        </div>
        <div>
          {renderLabel('Reference Number')}
          <Input
            value={formData.referenceNumber}
            onChange={(e) => updateField('referenceNumber', e.target.value)}
            placeholder="e.g., CAB/1/2025"
            className="font-mono"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        {renderLabel('Description')}
        <textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="Brief summary of the cabinet decision..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={3}
        />
      </div>

      {/* Category and Originating Ministry */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {renderLabel('Category', true)}
          <Select
            value={formData.category}
            onChange={(e) => updateField('category', e.target.value)}
          >
            <option value="">Select category</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </Select>
          {renderError('category')}
        </div>
        <div>
          {renderLabel('Originating Ministry')}
          <Select
            value={formData.originatingMinistry}
            onChange={(e) => updateField('originatingMinistry', e.target.value)}
          >
            <option value="">Select ministry</option>
            {MINISTRIES.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Priority (colored radio cards) */}
      <div>
        {renderSectionHeading('Priority Level *')}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PRIORITIES.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => updateField('priority', p.id)}
              className={clsx(
                'p-3 rounded-lg border-2 text-left transition-all',
                formData.priority === p.id
                  ? `border-${p.color}-500 bg-${p.color}-50`
                  : 'border-slate-200 hover:border-slate-300'
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={clsx(
                  'text-sm font-bold',
                  formData.priority === p.id ? `text-${p.color}-700` : 'text-slate-700'
                )}>
                  {p.label}
                </span>
                {formData.priority === p.id && (
                  <SafeIcon icon={FiIcons.FiCheckCircle} className={`text-${p.color}-600`} />
                )}
              </div>
              <p className="text-xs text-slate-500">{p.description}</p>
            </button>
          ))}
        </div>
        {renderError('priority')}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {renderLabel('Date Decided', true)}
          <Input
            type="date"
            value={formData.dateDecided}
            onChange={(e) => updateField('dateDecided', e.target.value)}
          />
          {renderError('dateDecided')}
        </div>
        <div>
          {renderLabel('Implementation Deadline', true)}
          <Input
            type="date"
            value={formData.deadline}
            onChange={(e) => updateField('deadline', e.target.value)}
          />
          {renderError('deadline')}
        </div>
      </div>

      {/* Responsible Ministries (multi-select with role assignment) */}
      <div>
        {renderSectionHeading('Responsible Ministries *')}
        <p className="text-xs text-slate-500 mb-3">
          Select ministries and assign their roles (Lead, Co-lead, or Support).
        </p>
        <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3">
          {MINISTRIES.map(ministry => {
            const selected = formData.responsibleMinistries.find(m => m.ministryId === ministry.id);
            return (
              <div
                key={ministry.id}
                className={clsx(
                  'flex items-center gap-3 p-2.5 rounded-lg border transition-colors',
                  selected
                    ? 'border-grn-navy/30 bg-grn-navy/5'
                    : 'border-transparent hover:bg-slate-50'
                )}
              >
                <input
                  type="checkbox"
                  checked={!!selected}
                  onChange={() => toggleMinistry(ministry.id)}
                  className="w-4 h-4 text-grn-navy rounded focus:ring-2 focus:ring-grn-navy/20 flex-shrink-0"
                />
                <span className="text-sm text-slate-700 flex-1 min-w-0 truncate">
                  <span className="font-mono text-xs text-slate-400 mr-1.5">{ministry.id}</span>
                  {ministry.name}
                </span>
                {selected && (
                  <select
                    value={selected.role}
                    onChange={(e) => updateMinistryRole(ministry.id, e.target.value)}
                    className="text-xs border border-slate-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-grn-navy/20 flex-shrink-0"
                  >
                    {MINISTRY_ROLES.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
        {formData.responsibleMinistries.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {formData.responsibleMinistries.map(m => (
              <span
                key={m.ministryId}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-grn-navy/10 text-grn-navy rounded-full"
              >
                {m.ministryId}
                <span className="text-grn-navy/60">({m.role})</span>
                <button
                  type="button"
                  onClick={() => toggleMinistry(m.ministryId)}
                  className="ml-0.5 hover:text-red-600 transition-colors"
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </span>
            ))}
          </div>
        )}
        {renderError('responsibleMinistries')}
      </div>

      {/* NDP6 Alignment (cascading dropdowns) */}
      <div>
        {renderSectionHeading('NDP6 Alignment')}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            {renderLabel('Pillar')}
            <Select
              value={formData.ndp6PillarId}
              onChange={(e) => {
                updateField('ndp6PillarId', e.target.value);
                updateField('ndp6ThematicAreaId', '');
                updateField('ndp6FocusArea', '');
              }}
            >
              <option value="">Select pillar</option>
              {NDP6_TAXONOMY.pillars.map(pillar => (
                <option key={pillar.id} value={String(pillar.id)}>{pillar.name}</option>
              ))}
            </Select>
          </div>
          <div>
            {renderLabel('Thematic Area')}
            <Select
              value={formData.ndp6ThematicAreaId}
              onChange={(e) => {
                updateField('ndp6ThematicAreaId', e.target.value);
                updateField('ndp6FocusArea', '');
              }}
              disabled={!formData.ndp6PillarId}
            >
              <option value="">
                {formData.ndp6PillarId ? 'Select thematic area' : 'Select a pillar first'}
              </option>
              {thematicAreas.map(ta => (
                <option key={ta.id} value={String(ta.id)}>{ta.name}</option>
              ))}
            </Select>
          </div>
          <div>
            {renderLabel('Focus Area')}
            <Select
              value={formData.ndp6FocusArea}
              onChange={(e) => updateField('ndp6FocusArea', e.target.value)}
              disabled={!formData.ndp6ThematicAreaId}
            >
              <option value="">
                {formData.ndp6ThematicAreaId ? 'Select focus area' : 'Select a thematic area first'}
              </option>
              {focusAreas.map(fa => (
                <option key={fa} value={fa}>{fa}</option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* Legal Basis */}
      <div>
        {renderLabel('Legal Basis')}
        <Input
          value={formData.legalBasis}
          onChange={(e) => updateField('legalBasis', e.target.value)}
          placeholder="e.g., Cabinet Resolution No. 1/15.03.25"
        />
      </div>

      {/* Budget Implication */}
      <div>
        {renderSectionHeading('Budget Implication')}
        <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
          <input
            type="checkbox"
            checked={formData.hasBudgetImpact}
            onChange={(e) => updateField('hasBudgetImpact', e.target.checked)}
            className="w-4 h-4 text-grn-navy rounded focus:ring-2 focus:ring-grn-navy/20 mt-0.5"
          />
          <div className="flex-1">
            <span className="text-sm font-bold text-slate-800">This decision has budget implications</span>
            <p className="text-xs text-slate-500 mt-0.5">Specify the estimated cost and funding source below.</p>
          </div>
        </label>
        {formData.hasBudgetImpact && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div>
              {renderLabel('Estimated Cost (NAD)')}
              <Input
                type="number"
                value={formData.estimatedCost}
                onChange={(e) => updateField('estimatedCost', e.target.value)}
                placeholder="e.g., 2500000000"
              />
            </div>
            <div>
              {renderLabel('Funding Source')}
              <Input
                value={formData.fundingSource}
                onChange={(e) => updateField('fundingSource', e.target.value)}
                placeholder="e.g., National Budget"
              />
            </div>
          </div>
        )}
      </div>

      {/* Notes / Full Text */}
      <div>
        {renderLabel('Resolution Full Text / Notes')}
        <textarea
          value={formData.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="Enter the complete resolution text or additional notes..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={4}
        />
      </div>
    </>
  );

  // --- Directive mode form ---

  const renderDirectiveForm = () => (
    <>
      {/* Title */}
      <div>
        {renderLabel('Directive Title', true)}
        <Input
          value={formData.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="e.g., Identify and allocate serviced land in all 14 regions"
        />
        {renderError('title')}
      </div>

      {/* Description */}
      <div>
        {renderLabel('Description')}
        <textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="Detailed description of what must be accomplished..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={3}
        />
      </div>

      {/* Parent Decision */}
      <div>
        {renderLabel('Parent Decision ID', true)}
        <Input
          value={formData.parentDecisionId}
          onChange={(e) => updateField('parentDecisionId', e.target.value)}
          placeholder="e.g., CAB-2025-001"
          className="font-mono"
        />
        {renderError('parentDecisionId')}
      </div>

      {/* Assigned Entity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {renderLabel('Entity Type', true)}
          <Select
            value={formData.assignedEntityType}
            onChange={(e) => updateField('assignedEntityType', e.target.value)}
          >
            {ENTITY_TYPES.map(et => (
              <option key={et.id} value={et.id}>{et.label}</option>
            ))}
          </Select>
        </div>
        <div>
          {renderLabel('Assigned Entity', true)}
          {formData.assignedEntityType === 'Ministry' ? (
            <Select
              value={formData.assignedEntityId}
              onChange={(e) => updateField('assignedEntityId', e.target.value)}
            >
              <option value="">Select ministry</option>
              {MINISTRIES.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
          ) : (
            <Input
              value={formData.assignedEntityId}
              onChange={(e) => updateField('assignedEntityId', e.target.value)}
              placeholder={`e.g., ${formData.assignedEntityType} name or code`}
            />
          )}
          {renderError('assignedEntityId')}
        </div>
      </div>

      {/* Assignee Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {renderLabel('Assignee Name')}
          <Input
            value={formData.assigneeName}
            onChange={(e) => updateField('assigneeName', e.target.value)}
            placeholder="e.g., Mr. F. Nghidishange"
          />
        </div>
        <div>
          {renderLabel('Assignee Role')}
          <Input
            value={formData.assigneeRole}
            onChange={(e) => updateField('assigneeRole', e.target.value)}
            placeholder="e.g., Executive Director"
          />
        </div>
      </div>

      {/* Deadline and Target Metric */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {renderLabel('Deadline', true)}
          <Input
            type="date"
            value={formData.deadline}
            onChange={(e) => updateField('deadline', e.target.value)}
          />
          {renderError('deadline')}
        </div>
        <div>
          {renderLabel('Target Metric')}
          <Input
            value={formData.targetMetric}
            onChange={(e) => updateField('targetMetric', e.target.value)}
            placeholder="e.g., 14 regions surveyed"
          />
        </div>
      </div>

      {/* Milestones (dynamic list) */}
      <div>
        {renderSectionHeading('Milestones')}
        <div className="space-y-3">
          {formData.milestones.map((ms, index) => (
            <div key={ms.id} className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50/50">
              <span className="text-xs font-mono text-slate-400 mt-2.5 flex-shrink-0">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  value={ms.title}
                  onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                  placeholder="Milestone title"
                />
                <Input
                  type="date"
                  value={ms.deadline}
                  onChange={(e) => updateMilestone(index, 'deadline', e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeMilestone(index)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0 mt-1"
              >
                <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addMilestone}
          className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          <SafeIcon icon={FiIcons.FiPlus} />
          Add Milestone
        </button>
      </div>

      {/* Notes */}
      <div>
        {renderLabel('Notes')}
        <textarea
          value={formData.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="Additional notes or instructions..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={3}
        />
      </div>
    </>
  );

  // --- Assignment mode form ---

  const renderAssignmentForm = () => (
    <>
      {/* Task Description */}
      <div>
        {renderLabel('Task Description', true)}
        <textarea
          value={formData.taskDescription}
          onChange={(e) => updateField('taskDescription', e.target.value)}
          placeholder="e.g., Complete land survey and identification in Khomas Region"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={3}
        />
        {renderError('taskDescription')}
      </div>

      {/* Parent Directive */}
      <div>
        {renderLabel('Parent Directive ID', true)}
        <Input
          value={formData.parentDirectiveId}
          onChange={(e) => updateField('parentDirectiveId', e.target.value)}
          placeholder="e.g., DIR-2025-001-01"
          className="font-mono"
        />
        {renderError('parentDirectiveId')}
      </div>

      {/* Assignee Details */}
      <div>
        {renderSectionHeading('Assignee Details')}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {renderLabel('Full Name', true)}
            <Input
              value={formData.assigneeName}
              onChange={(e) => updateField('assigneeName', e.target.value)}
              placeholder="e.g., Mr. K. Nepemba"
            />
            {renderError('assigneeName')}
          </div>
          <div>
            {renderLabel('Role')}
            <Input
              value={formData.assigneeRole}
              onChange={(e) => updateField('assigneeRole', e.target.value)}
              placeholder="e.g., Regional Land Surveyor"
            />
          </div>
          <div>
            {renderLabel('Title')}
            <Input
              value={formData.assigneeTitle}
              onChange={(e) => updateField('assigneeTitle', e.target.value)}
              placeholder="e.g., Deputy Director: Land Use Planning"
            />
          </div>
          <div>
            {renderLabel('Ministry', true)}
            <Select
              value={formData.assigneeMinistry}
              onChange={(e) => updateField('assigneeMinistry', e.target.value)}
            >
              <option value="">Select ministry</option>
              {MINISTRIES.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
            {renderError('assigneeMinistry')}
          </div>
          <div>
            {renderLabel('Directorate')}
            <Input
              value={formData.assigneeDirectorate}
              onChange={(e) => updateField('assigneeDirectorate', e.target.value)}
              placeholder="e.g., Land Use Planning Directorate"
            />
          </div>
          <div>
            {renderLabel('Email')}
            <Input
              type="email"
              value={formData.assigneeEmail}
              onChange={(e) => updateField('assigneeEmail', e.target.value)}
              placeholder="e.g., k.nepemba@murdh.gov.na"
            />
          </div>
        </div>
      </div>

      {/* Deadline */}
      <div>
        {renderLabel('Deadline', true)}
        <Input
          type="date"
          value={formData.deadline}
          onChange={(e) => updateField('deadline', e.target.value)}
          className="max-w-xs"
        />
        {renderError('deadline')}
      </div>

      {/* Checklist (dynamic add/remove list) */}
      <div>
        {renderSectionHeading('Checklist Items')}
        <div className="space-y-2">
          {formData.checklistItems.map((ci, index) => (
            <div key={ci.id} className="flex items-center gap-3">
              <span className="text-xs font-mono text-slate-400 flex-shrink-0 w-6 text-right">
                {String(index + 1).padStart(2, '0')}
              </span>
              <Input
                value={ci.item}
                onChange={(e) => updateChecklistItem(index, e.target.value)}
                placeholder="Checklist item description"
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => removeChecklistItem(index)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
              >
                <SafeIcon icon={FiIcons.FiTrash2} className="text-sm" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addChecklistItem}
          className="mt-3 flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
        >
          <SafeIcon icon={FiIcons.FiPlus} />
          Add Checklist Item
        </button>
      </div>

      {/* Notes */}
      <div>
        {renderLabel('Notes')}
        <textarea
          value={formData.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="Additional notes or instructions for the assignee..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={3}
        />
      </div>
    </>
  );

  // --- Main render ---

  const modeLabels = { decision: 'Decision', directive: 'Directive', assignment: 'Assignment' };
  const submitLabel = isEditing
    ? `Update ${modeLabels[mode]}`
    : `Create ${modeLabels[mode]}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className={clsx(
              'px-6 py-4 border-b flex justify-between items-start',
              config.headerBg,
              config.headerBorder
            )}>
              <div className="flex items-start gap-3">
                <div className={clsx('p-2 rounded-lg', config.iconBg)}>
                  <SafeIcon icon={config.icon} className={clsx(config.iconColor, 'text-xl')} />
                </div>
                <div>
                  <h2 className={clsx('text-xl font-bold', config.titleColor)}>
                    {config.title}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {mode === 'decision' && 'Create a new cabinet decision record for tracking and implementation monitoring.'}
                    {mode === 'directive' && 'Add a directive to cascade a cabinet decision into actionable instructions.'}
                    {mode === 'assignment' && 'Assign an operational task to an individual officer for implementation.'}
                  </p>
                  {isEditing && existingData?.id && (
                    <div className="mt-2">
                      <Badge status="Draft" size="sm">{existingData.id}</Badge>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              {mode === 'decision' && renderDecisionForm()}
              {mode === 'directive' && renderDirectiveForm()}
              {mode === 'assignment' && renderAssignmentForm()}
            </form>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-slate-50">
              <div className="text-xs text-slate-500">
                {isEditing ? 'Changes will be logged' : 'New record will be created'} at{' '}
                {format(new Date(), 'dd/MM/yyyy HH:mm')}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  icon={isEditing ? FiIcons.FiSave : FiIcons.FiPlus}
                  onClick={handleSubmit}
                >
                  {submitLabel}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
